import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, PlayCircle, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { Can } from "../../components/Can";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const VAZIO = { title: "", description: "", video: "", position: 0 };

const Helps = () => {
	const { user } = React.useContext(AuthContext);

	const [helps, setHelps] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);
	const [dados, setDados] = useState(VAZIO);
	const [salvando, setSalvando] = useState(false);
	const [assistindo, setAssistindo] = useState(null);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/helps");
			setHelps(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const abrirNovo = () => {
		setSelecionado(null);
		setDados(VAZIO);
		setModalOpen(true);
	};

	const abrirEdicao = (help) => {
		setSelecionado(help);
		setDados({
			title: help.title || "",
			description: help.description || "",
			video: help.video || "",
			position: help.position || 0,
		});
		setModalOpen(true);
	};

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			if (selecionado) {
				await api.put(`/helps/${selecionado.id}`, dados);
				toast.success("Item alterado.");
			} else {
				await api.post("/helps", dados);
				toast.success("Item criado.");
			}
			setModalOpen(false);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/helps/${id}`);
			toast.success("Item removido.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Remover "${selecionado.title}"?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={() => excluir(selecionado.id)}
			>
				Some da central de ajuda para toda a equipe.
			</ConfirmationModal>

			<MainHeader>
				<Title>Ajuda</Title>
				<MainHeaderButtonsWrapper>
					<Can
						role={user.profile}
						perform="drawer-admin-items:view"
						yes={() => (
							<Button onClick={abrirNovo}>
								<Plus className="h-4 w-4" />
								Novo item
							</Button>
						)}
					/>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Explicações e vídeos que a sua empresa escreve para a própria equipe.
					Serve para quem entrou agora não precisar perguntar a mesma coisa
					duas vezes.
				</p>

				{carregando ? (
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-44 rounded-xl" />
						))}
					</div>
				) : helps.length === 0 ? (
					<div className="rounded-xl border bg-card py-12 text-center">
						<p className="text-sm text-muted-foreground">
							Nada por aqui ainda.
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							Quem administra a conta pode adicionar o primeiro item com um
							vídeo do YouTube ou só um texto.
						</p>
					</div>
				) : (
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{helps.map((help) => (
							<div
								key={help.id}
								className="flex flex-col overflow-hidden rounded-xl border bg-card"
							>
								{help.video && (
									<button
										type="button"
										onClick={() => setAssistindo(help)}
										className="group relative aspect-video w-full overflow-hidden bg-muted"
										title={`Assistir: ${help.title}`}
									>
										<img
											src={`https://img.youtube.com/vi/${help.video}/hqdefault.jpg`}
											alt=""
											className="h-full w-full object-cover transition-transform group-hover:scale-105"
										/>
										<span className="absolute inset-0 flex items-center justify-center bg-black/30">
											<PlayCircle className="h-12 w-12 text-white" />
										</span>
									</button>
								)}

								<div className="flex flex-1 flex-col p-4">
									<h3 className="text-sm font-semibold">{help.title}</h3>
									{help.description && (
										<p className="mt-1 flex-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
											{help.description}
										</p>
									)}

									<Can
										role={user.profile}
										perform="drawer-admin-items:view"
										yes={() => (
											<div className="mt-3 flex justify-end gap-1 border-t pt-2">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Editar item"
													title="Editar item"
													onClick={() => abrirEdicao(help)}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-destructive hover:text-destructive"
													aria-label="Remover item"
													title="Remover item"
													onClick={() => {
														setSelecionado(help);
														setConfirmOpen(true);
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										)}
									/>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Player */}
			<Dialog
				open={Boolean(assistindo)}
				onOpenChange={(aberto) => !aberto && setAssistindo(null)}
			>
				<DialogContent className="sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>{assistindo?.title}</DialogTitle>
					</DialogHeader>
					{assistindo?.video && (
						<div className="aspect-video w-full overflow-hidden rounded-lg">
							<iframe
								className="h-full w-full"
								src={`https://www.youtube.com/embed/${assistindo.video}`}
								title={assistindo.title}
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen
							/>
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Cadastro */}
			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-lg">
					<form onSubmit={salvar}>
						<DialogHeader>
							<DialogTitle>
								{selecionado ? "Editar item" : "Novo item"}
							</DialogTitle>
							<DialogDescription>
								O vídeo é opcional — um item só com texto também aparece.
							</DialogDescription>
						</DialogHeader>

						<div className="grid gap-3 py-4">
							<div>
								<Label htmlFor="title">Título</Label>
								<Input
									id="title"
									required
									value={dados.title}
									onChange={(e) =>
										setDados({ ...dados, title: e.target.value })
									}
								/>
							</div>
							<div>
								<Label htmlFor="description">Explicação</Label>
								<Textarea
									id="description"
									rows={4}
									value={dados.description}
									onChange={(e) =>
										setDados({ ...dados, description: e.target.value })
									}
								/>
							</div>
							<div>
								<Label htmlFor="video">Vídeo do YouTube</Label>
								<Input
									id="video"
									placeholder="Cole o endereço do vídeo"
									value={dados.video}
									onChange={(e) =>
										setDados({ ...dados, video: e.target.value })
									}
								/>
								<p className="mt-1 text-xs text-muted-foreground">
									Pode colar o endereço inteiro da barra do navegador — o
									sistema tira o código sozinho.
								</p>
							</div>
							<div>
								<Label htmlFor="position">Ordem</Label>
								<Input
									id="position"
									type="number"
									className="w-24"
									value={dados.position}
									onChange={(e) =>
										setDados({ ...dados, position: e.target.value })
									}
								/>
								<p className="mt-1 text-xs text-muted-foreground">
									Menor aparece primeiro.
								</p>
							</div>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="ghost"
								onClick={() => setModalOpen(false)}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={salvando}>
								{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
								Salvar
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default Helps;
